import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api.js';
import { PageLayout } from '@/components/layout/PageLayout.js';

type Evento = {
  id: string;
  titulo: string;
  descricao?: string | null;
  inicio_em?: string | null;
  minha_participacao?: string | null;
};

export function EventosPage() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['eventos'],
    queryFn: async () => (await api.get<Evento[]>('/conteudo/eventos')).data,
  });

  const confirmar = useMutation({
    mutationFn: async (id: string) => (await api.post(`/conteudo/eventos/${id}/confirmar`)).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['eventos'] });
      void qc.invalidateQueries({ queryKey: ['dashboard', 'resumo'] });
    },
  });

  const cancelar = useMutation({
    mutationFn: async (id: string) => (await api.post(`/conteudo/eventos/${id}/cancelar`)).data,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['eventos'] });
      void qc.invalidateQueries({ queryKey: ['dashboard', 'resumo'] });
    },
  });

  return (
    <PageLayout
      title="Eventos"
      description="Agenda sincronizada do HackOS — workshops, lives e encontros do ecossistema."
    >
      {q.isLoading && <p className="text-ha-subtle">Carregando…</p>}
      {q.isError && <p className="text-feedback">Não foi possível carregar os eventos.</p>}
      {!q.data?.length && !q.isLoading && !q.isError && (
        <p className="text-sm text-ha-muted">Nenhum evento sincronizado do HackOS ainda.</p>
      )}
      <ul className="flex flex-col gap-3">
        {(q.data ?? []).map((e) => {
          const confirmado = e.minha_participacao === 'CONFIRMADO';
          const cancelado = e.minha_participacao === 'CANCELADO';
          return (
            <li key={e.id} className="rounded-card border border-ha-border2 bg-ha-card p-4">
              <h2 className="font-semibold text-ha-text">{e.titulo}</h2>
              {e.inicio_em ? (
                <p className="mt-1 text-xs text-ha-muted">
                  {new Date(e.inicio_em).toLocaleString('pt-BR')}
                </p>
              ) : null}
              {e.descricao ? <p className="mt-2 text-sm text-ha-subtle">{e.descricao}</p> : null}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {confirmado ? (
                  <>
                    <span className="text-xs font-semibold text-emerald-600">Presença confirmada</span>
                    <button
                      type="button"
                      className="text-xs text-ha-muted underline"
                      disabled={cancelar.isPending}
                      onClick={() => cancelar.mutate(e.id)}
                    >
                      Cancelar presença
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="rounded-element px-3 py-1.5 text-[10px] font-bold uppercase text-black disabled:opacity-50"
                    style={{ background: '#ffc800' }}
                    disabled={confirmar.isPending}
                    onClick={() => confirmar.mutate(e.id)}
                  >
                    Confirmar presença
                  </button>
                )}
                {cancelado && !confirmado && (
                  <span className="text-xs text-ha-muted">Participação cancelada</span>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </PageLayout>
  );
}
